import { Fragment, ComponentPropsWithoutRef, ElementType, ReactNode, isValidElement } from 'react'
import i18next, { ParseKeys } from 'i18next'
import { Trans, TransProps, initReactI18next, useTranslation } from 'react-i18next'
import { pickBy, isEmpty } from './utils'

export { I18nextProvider, useTranslation } from 'react-i18next'

export const i18n = i18next.use(initReactI18next)

export type MessageProps<T extends ElementType> = {
  i18nKey: ParseKeys
  component?: T
  values?: TransProps<ParseKeys>['values'] & TransProps<ParseKeys>['components']
  renderOption?: (msg: ReactNode, idx: number) => ReactNode
} & Omit<ComponentPropsWithoutRef<T>, 'children'>

export const Message = <T extends ElementType>({
  i18nKey,
  component: Component,
  values: valuesProps,
  renderOption,
  ...props
}: MessageProps<T>) => {
  const { t } = useTranslation()

  const values = pickBy(valuesProps, !isValidElement) as TransProps<ParseKeys>['values']
  const components = pickBy(valuesProps, isValidElement) as TransProps<ParseKeys>['components']

  const s = t(i18nKey, values)

  if (!isEmpty(components)) {
    const ParentComponent = Component
      ? ({ children }: { children: ReactNode }) => {
          // @ts-ignore
          return <Component {...props}>{children}</Component>
        }
      : undefined

    if (Array.isArray(s))
      return (
        <>
          {s.map((_, i) => (
            <Trans
              key={i}
              parent={ParentComponent}
              components={components}
            >{`${i18nKey}.${i}`}</Trans>
          ))}
        </>
      )
    return (
      <Trans parent={ParentComponent} components={components}>
        {i18nKey}
      </Trans>
    )
  }

  if (Array.isArray(s)) {
    if (renderOption)
      return (
        <>
          {s.map((c, i) => (
            <Fragment key={i}>{renderOption(c, i)}</Fragment>
          ))}
        </>
      )

    return s.map((c, i: number) =>
      Component ? (
        // @ts-ignore
        <Component key={i} {...props}>
          {c}
        </Component>
      ) : (
        <Fragment key={i}>{c}</Fragment>
      ),
    )
  }

  if (Component) {
    // @ts-ignore
    return <Component {...props}>{s}</Component>
  }

  return <>{s}</>
}

export default Message
